<template>
  <div
    v-loading="shouldShowLoading"
    :element-loading-text="$t('transcription.loading')"
    class="transcription-content"
  >
    <div class="transcription-title-wrapper">
      <h3 class="transcription-title">
        {{ i18n.t('transcription.transcribe') }}
      </h3>
      <BCopyButton
        :disabled="processingStatus !== 'succeeded'"
        :text="transcriptionCopyText"
        :title="i18n.t('transcription.transcribe')"
        is-detailed-name
      />
    </div>
    <template v-if="!shouldShowLoading">
      <BEmptyBox
        v-if="isTranscriptionResultEmpty"
        display-only
        class="my-500"
      >
        {{ i18n.t('transcription.transcriptionResultIsEmpty') }}
      </BEmptyBox>
      <template v-else-if="processingStatus === 'succeeded'">
        <div class="transcription-content-container">
          <BLayout
            v-for="(segment, index) in transcription?.mergedTexts"
            :key="index"
            class="my-500"
          >
            <div
              class="time"
              @click="() => handleClickPlayAt(segment.start)"
            >
              {{ formatSecondsToTime(segment.start) }}
            </div>
            <div class="speaker-text">
              <div class="speaker">
                <BIcon
                  type="white"
                  class="speaker-icon"
                  :style="{ backgroundColor: computeColorBySpeaker(segment.speaker) }"
                >
                  person
                </BIcon>
                {{ computeSpeakerName(segment.speaker) }}
              </div>
              <div class="text">
                {{ segment.text }}
              </div>
            </div>
          </BLayout>
        </div>
      </template>
      <template v-else-if="processingStatus === 'failed'">
        <BError
          :error-title="i18n.t('general.error')"
          :error-info="i18n.t('transcription.transcribeFailed')"
          class="mt-100"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { DeepReadonly, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Transcription } from '@/api/openapi';
import { useTranscriptionSpeakers } from '@/composable/user/transcription/transcriptionSpeakers';
import { formatSecondsToTime } from '@/utils/date-time';

type TProcessingStatus = 'not_started' | 'processing' | 'succeeded' | 'failed';

type Props = {
  transcription: DeepReadonly<Transcription> | null;
  isTranscriptionResultEmpty: boolean;
};
type TEmits = {
  'click:playAt': [number];
};

const props = defineProps<Props>();
const emit = defineEmits<TEmits>();

const i18n = useI18n();

const { computeColorBySpeaker, computeSpeakerName } = useTranscriptionSpeakers(() => props.transcription);

const processingStatus = computed<TProcessingStatus>(() => {
  return props.transcription?.processingStatus ?? 'processing';
});

const shouldShowLoading = computed(() => {
  return processingStatus.value === 'processing' || processingStatus.value === 'not_started';
});

const transcriptionCopyText = computed(() => {
  return props.transcription?.mergedTexts?.map((segment) =>
    `${formatSecondsToTime(segment.start)} ${computeSpeakerName(segment.speaker)}\n${segment.text}`,
  ).join('\n\n');
});

const handleClickPlayAt = (start: number) => {
  emit('click:playAt', start);
};
</script>

<style lang="scss" scoped>
.transcription-content {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.transcription-title-wrapper {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

.transcription-title {
  font-size: $fontsize-200;
}

.time {
  width: 100px;
  height: 30px;
  display: flex;
  align-items: center;
  color: $textcolor-light;
  font-family: 'Noto Sans JP', sans-serif;
  cursor: pointer;
}

.speaker-text {
  width: 100%;

  .speaker {
    display: flex;
    align-items: center;
    height: 30px;
  }
  .speaker-icon {
    $size: 24px;
    @include m-fixed-width($size);
    @include m-fixed-height($size);
    margin-right: 8px;
    border-radius: 12px;
  }
  .text {
    margin-top: 5px;
  }
}

.transcription-content-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
